<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AuthBrandPanel from '@/components/authentication/AuthBrandPanel.vue'
import LanguageSwitcherHorizontal from '@/components/common/LanguageSwitcherHorizontal.vue'
import { getSecretKey, removeSecretKey } from '@/services/api/auth.js'
import { getDeviceInfo } from '@/utils/device'
import { useAuthStore } from '@/stores/authStore.js'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

const user = ref(null)
const showSecret = ref(false)
const device = ref({})
const activeStep = ref(1)

const twoFactorBrandContent = computed(() => ({
    name: t('components.authentication.twoFactor.brand.name'),
    badge: t('components.authentication.twoFactor.brand.badge'),
    title: t('components.authentication.twoFactor.brand.title'),
    accent: t('components.authentication.twoFactor.brand.accent'),
    description: t('components.authentication.twoFactor.brand.description'),
    logo: {
        type: 'image',
        src: '/images/static/logos/logo-icon-white.webp',
        alt: t('components.authentication.twoFactor.brand.name'),
    },
    highlights: [
        {
            icon: 'phone_iphone',
            title: t('components.authentication.twoFactor.brand.mobileCodeTitle'),
            description: t('components.authentication.twoFactor.brand.mobileCodeDescription'),
        },
        {
            icon: 'enhanced_encryption',
            title: t('components.authentication.twoFactor.brand.advancedProtectionTitle'),
            description: t('components.authentication.twoFactor.brand.advancedProtectionDescription'),
        },
        {
            icon: 'timer',
            title: t('components.authentication.twoFactor.brand.secondsCodeTitle'),
            description: t('components.authentication.twoFactor.brand.secondsCodeDescription'),
        },
    ],
}))

const steps = computed(() => [
    {
        number: 1,
        icon: 'install_mobile',
        title: t('components.authentication.twoFactor.installApp'),
    },
    {
        number: 2,
        icon: 'qr_code_scanner',
        title: t('components.authentication.twoFactor.linkAccount'),
    },
    {
        number: 3,
        icon: 'verified',
        title: t('components.authentication.twoFactor.finalizeAccess'),
    },
])

onMounted(() => {
    user.value = getSecretKey()
    device.value = getDeviceInfo()
})

const isDesktop = computed(() => device.value.os === 'desktop')
const showSvg = computed(() => showSecret.value && isDesktop.value && user.value?.svg2fa)
const showKey = computed(() => showSecret.value && user.value?.secretKey)

const goToStep = (step) => {
    activeStep.value = step
}

const nextStep = () => {
    activeStep.value = Math.min(activeStep.value + 1, steps.value.length)
}

const previousStep = () => {
    activeStep.value = Math.max(activeStep.value - 1, 1)
}

const copyKey = () => {
    if (!user.value?.secretKey) {
        return
    }

    navigator.clipboard.writeText(user.value.secretKey)
        .then(() => message.info(t('common.messages.info.messageCopied')))
        .catch(() => message.error(t('common.messages.errors.errorCopyingMessage')))
}

const finalizeTwoFactor = () => {
    removeSecretKey()
    router.push(authStore.signInRoute)
}
</script>

<template>
    <div class="two-factor-page">
        <AuthBrandPanel :content="twoFactorBrandContent" />

        <main class="setup-panel">
            <section class="setup-panel__content">
                <div class="setup-panel__header">
                    <h2>{{ t('components.authentication.twoFactor.configureVerification') }}</h2>
                    <p>{{ t('components.authentication.twoFactor.configureVerificationSubtitle') }}</p>
                </div>

                <div class="setup-card">
                    <nav class="setup-steps" :aria-label="t('components.authentication.twoFactor.stepsAriaLabel')">
                        <button
                            v-for="step in steps"
                            :key="step.number"
                            type="button"
                            :class="[
                                'setup-steps__item',
                                { 'setup-steps__item--active': activeStep === step.number },
                                { 'setup-steps__item--done': activeStep > step.number },
                            ]"
                            @click="goToStep(step.number)"
                        >
                            <span class="setup-steps__number">
                                <span v-if="activeStep <= step.number">{{ step.number }}</span>
                                <span v-else class="material-symbols-outlined">check</span>
                            </span>
                            <span>
                                <strong>{{ step.title }}</strong>
                            </span>
                        </button>
                    </nav>

                    <div v-if="activeStep === 1" class="setup-step">
                        <div class="setup-step__title">
                            <span class="material-symbols-outlined">install_mobile</span>
                            <div>
                                <h3>{{ t('components.authentication.twoFactor.downloadAuthenticator') }}</h3>
                                <p>{{ t('components.authentication.twoFactor.downloadAuthenticatorDescription') }}</p>
                            </div>
                        </div>

                        <div v-if="isDesktop" class="store-grid">
                            <article>
                                <strong>Android</strong>
                                <v-img
                                    src="/images/static/QRCode_GoogleAuthenticator_Android.svg"
                                    alt="QR Code Android"
                                />
                            </article>
                            <article>
                                <strong>iOS</strong>
                                <v-img
                                    src="/images/static/QRCode_GoogleAuthenticator_Apple.svg"
                                    alt="QR Code iOS"
                                />
                            </article>
                        </div>

                        <div class="store-links">
                            <a
                                v-if="device.os === 'android' || device.os === 'unknown'"
                                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span class="material-symbols-outlined">shop</span>
                                Play Store
                            </a>
                            <a
                                v-if="device.os === 'ios' || device.os === 'unknown'"
                                href="https://apps.apple.com/br/app/google-authenticator/id388497605"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span class="material-symbols-outlined">phone_iphone</span>
                                App Store
                            </a>
                        </div>
                    </div>

                    <div v-else-if="activeStep === 2" class="setup-step">
                        <div class="setup-step__title">
                            <span class="material-symbols-outlined">qr_code_scanner</span>
                            <div>
                                <h3>{{ t('components.authentication.twoFactor.linkAccount') }}</h3>
                                <p>{{ isDesktop ? t('components.authentication.twoFactor.desktopLinkDescription') : t('components.authentication.twoFactor.mobileLinkDescription') }}</p>
                            </div>
                        </div>

                        <button class="secondary-action" type="button" @click="showSecret = !showSecret">
                            <span class="material-symbols-outlined">{{ showSecret ? 'visibility_off' : 'visibility' }}</span>
                            {{ showSecret ? t('components.authentication.twoFactor.hideSecretKey') : t('components.authentication.twoFactor.showSecretKey') }}
                        </button>

                        <div v-if="showSecret" class="secret-warning">
                            <span class="material-symbols-outlined">warning</span>
                            {{ t('components.authentication.twoFactor.ThisKeyWillOnlyBedisplayedThisOnceKeepItInaSafePlace') }}
                        </div>

                        <div v-if="showSvg" class="qr-box" v-html="user.svg2fa"></div>

                        <div v-if="showKey" class="secret-key">
                            <div>
                                <small>{{ t('components.authentication.twoFactor.secretKey') }}</small>
                                <strong>{{ user.secretKey }}</strong>
                            </div>
                            <button type="button" @click="copyKey" :aria-label="t('components.authentication.twoFactor.copySecretKey')">
                                <span class="material-symbols-outlined">content_copy</span>
                            </button>
                        </div>
                    </div>

                    <div v-else class="setup-step">
                        <div class="setup-step__title">
                            <span class="material-symbols-outlined">verified</span>
                            <div>
                                <h3>{{ t('components.authentication.twoFactor.finalizeSetup') }}</h3>
                                <p>{{ t('components.authentication.twoFactor.afterSettingUpAppLoginAgainToActivate') }}</p>
                            </div>
                        </div>

                        <div class="finish-box">
                            <span class="material-symbols-outlined">shield_lock</span>
                            <div>
                                <strong>{{ t('components.authentication.twoFactor.readyToActivate') }}</strong>
                                <small>{{ t('components.authentication.twoFactor.readyToActivateDescription') }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="setup-actions">
                        <button
                            v-if="activeStep > 1"
                            class="ghost-action"
                            type="button"
                            @click="previousStep"
                        >
                            <span class="material-symbols-outlined">arrow_back</span>
                            {{ t('components.authentication.twoFactor.back') }}
                        </button>
                        <span v-else></span>

                        <button
                            v-if="activeStep < steps.length"
                            class="primary-action"
                            type="button"
                            @click="nextStep"
                        >
                            {{ t('components.authentication.twoFactor.continue') }}
                            <span class="material-symbols-outlined">arrow_forward</span>
                        </button>
                        <button
                            v-else
                            class="primary-action"
                            type="button"
                            @click="finalizeTwoFactor"
                        >
                            {{ t('components.authentication.twoFactor.finalizeAndLogin') }}
                            <span class="material-symbols-outlined">login</span>
                        </button>
                    </div>
                </div>

                <LanguageSwitcherHorizontal class="setup-panel__languages" />
            </section>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.two-factor-page {
    min-height: 100vh;
    display: flex;
    font-family: Inter, sans-serif;
    background: #f8f9ff;
}

.setup-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 32px;
    background: #f8f9ff;

    &__content {
        width: 100%;
        max-width: 640px;
    }

    &__header {
        margin-bottom: 28px;
        text-align: center;

        h2 {
            margin: 0 0 8px;
            color: #260944;
            font-size: 28px;
            font-weight: 800;
            line-height: 1.2;
        }

        p {
            max-width: 420px;
            margin: 0 auto;
            color: #a9a9c8;
            font-size: 14px;
            line-height: 1.65;
        }
    }

    &__languages {
        margin-top: 18px;
    }
}

.setup-card {
    padding: 32px;
    border: 1px solid #eceef2;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 40px rgba(96, 93, 255, 0.1);
}

.setup-steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 28px;

    &__item {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 56px;
        padding: 10px;
        border: 1.5px solid #eceef2;
        border-radius: 12px;
        background: #f8f9ff;
        text-align: left;
        cursor: pointer;
        transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;

        strong {
            display: block;
        }

        strong {
            color: #260944;
            font-size: 13px;
            font-weight: 800;
            line-height: 1.25;
        }

        &--active {
            border-color: #ffd056;
            background: #fffdf3;
            box-shadow: 0 8px 22px rgba(255, 208, 86, 0.18);
        }

        &--done {
            border-color: rgba(23, 207, 151, 0.36);
            background: rgba(23, 207, 151, 0.08);
        }
    }

    &__number {
        width: 28px;
        height: 28px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 9px;
        background: #260944;
        color: #fff;
        font-size: 12px;
        font-weight: 800;

        .material-symbols-outlined {
            font-size: 17px;
        }
    }
}

.setup-step {
    min-height: 300px;

    &__title {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        margin-bottom: 22px;

        > span {
            width: 44px;
            height: 44px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 12px;
            background: rgba(255, 208, 86, 0.18);
            color: #8f7a0b;
            font-size: 24px;
        }

        h3 {
            margin: 0 0 4px;
            color: #260944;
            font-size: 20px;
            font-weight: 800;
        }

        p {
            margin: 0;
            color: #5b5b98;
            font-size: 13px;
            line-height: 1.6;
        }
    }
}

.store-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    article {
        padding: 18px;
        border: 1px solid #eceef2;
        border-radius: 12px;
        background: #f8f9ff;
        text-align: center;

        strong {
            display: block;
            margin-bottom: 12px;
            color: #260944;
            font-size: 14px;
            font-weight: 800;
        }

        :deep(.v-img) {
            max-width: 150px;
            margin: 0 auto;
        }
    }
}

.store-links {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;

    a {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 11px 14px;
        border: 1.5px solid #eceef2;
        border-radius: 10px;
        background: #fff;
        color: #260944;
        font-size: 13px;
        font-weight: 700;
        text-decoration: none;
    }

    .material-symbols-outlined {
        color: #8f7a0b;
        font-size: 19px;
    }
}

.secondary-action,
.ghost-action,
.primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 0;
    border-radius: 10px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
}

.secondary-action,
.ghost-action {
    padding: 12px 14px;
    background: #f8f9ff;
    color: #260944;
}

.primary-action {
    padding: 13px 16px;
    background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
    box-shadow: 0 6px 24px rgba(96, 93, 255, 0.2);
    color: #2c2c2cd0;
}

.secret-warning {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 14px;
    padding: 12px 14px;
    border: 1px solid rgba(255, 208, 86, 0.44);
    border-radius: 10px;
    background: rgba(255, 208, 86, 0.12);
    color: #6f5c00;
    font-size: 13px;
    line-height: 1.45;
}

.qr-box {
    width: fit-content;
    max-width: 100%;
    margin: 18px auto 0;
    padding: 16px;
    border: 1px solid #eceef2;
    border-radius: 14px;
    background: #fff;

    :deep(svg) {
        max-width: 210px;
        height: auto;
        display: block;
    }
}

.secret-key {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 18px;
    padding: 14px;
    border: 1px solid #eceef2;
    border-radius: 12px;
    background: #f8f9ff;

    small,
    strong {
        display: block;
    }

    small {
        margin-bottom: 4px;
        color: #a9a9c8;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
    }

    strong {
        color: #260944;
        font-size: 13px;
        line-height: 1.45;
        word-break: break-all;
    }

    button {
        width: 38px;
        height: 38px;
        flex-shrink: 0;
        border: 0;
        border-radius: 10px;
        background: #fff;
        color: #605dff;
        cursor: pointer;
    }
}

.finish-box {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px;
    border: 1px solid rgba(23, 207, 151, 0.28);
    border-radius: 14px;
    background: rgba(23, 207, 151, 0.08);

    > span {
        color: #0f9f73;
        font-size: 28px;
    }

    strong,
    small {
        display: block;
    }

    strong {
        margin-bottom: 4px;
        color: #260944;
        font-size: 15px;
        font-weight: 800;
    }

    small {
        color: #5b5b98;
        font-size: 13px;
        line-height: 1.5;
    }
}

.setup-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 26px;
    padding-top: 20px;
    border-top: 1px solid #eceef2;
}

@media only screen and (max-width: 960px) {
    .two-factor-page {
        flex-direction: column-reverse;
    }

    .setup-panel {
        padding: 42px 24px;
    }
}

@media only screen and (max-width: 680px) {
    .setup-card {
        padding: 22px;
    }

    .setup-steps {
        grid-template-columns: 1fr;

        &__item {
            min-height: auto;
        }
    }

    .setup-step {
        min-height: auto;
    }

    .store-grid {
        grid-template-columns: 1fr;
    }
}

@media only screen and (max-width: 575px) {
    .setup-panel {
        padding: 34px 16px;

        &__header {
            margin-bottom: 24px;

            h2 {
                font-size: 24px;
            }
        }
    }

    .setup-actions {
        align-items: stretch;
        flex-direction: column;

        > span {
            display: none;
        }
    }

    .primary-action,
    .ghost-action {
        width: 100%;
    }
}
</style>
